import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faFileAlt, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import s from '../CaseFilePage.module.css';

interface DraftTemplate {
  id: number;
  display_name: string;
  category?: string;
  recipient_type?: string;
  all_mapped?: boolean;
  [key: string]: unknown;
}

interface EmailTemplate {
  id: number;
  display_name: string;
  recipient_type?: string;
  subject_template?: string;
  [key: string]: unknown;
}

interface DraftAssistantModalProps {
  draftTab: 'letter' | 'email';
  setDraftTab: (t: 'letter' | 'email') => void;
  selectedTemplateId: string;
  setSelectedTemplateId: (v: string) => void;
  selectedEmailTemplateId: string;
  setSelectedEmailTemplateId: (v: string) => void;
  draftGenerating: boolean;
  draftTemplates: DraftTemplate[];
  emailTemplates: EmailTemplate[];
  handleGenerateLetter: () => void;
  handlePreviewEmail: () => void;
  canSendEmail?: boolean;
  onClose: () => void;
}

export default function DraftAssistantModal({
  draftTab,
  setDraftTab,
  selectedTemplateId,
  setSelectedTemplateId,
  selectedEmailTemplateId,
  setSelectedEmailTemplateId,
  draftGenerating,
  draftTemplates,
  emailTemplates,
  handleGenerateLetter,
  handlePreviewEmail,
  canSendEmail = true,
  onClose,
}: DraftAssistantModalProps) {
  return (
    <ModalOverlay onClose={onClose} title="Draft Assistant">
      <div className={s.modalForm}>
        {/* Tabs */}
        <div className={s.tabBar}>
          <button
            className={`${s.tab} ${draftTab === 'letter' ? s.tabActive : ''}`}
            onClick={() => setDraftTab('letter')}
          >
            Letter
          </button>
          {canSendEmail && (
            <button
              className={`${s.tab} ${draftTab === 'email' ? s.tabActive : ''}`}
              onClick={() => setDraftTab('email')}
            >
              Email
            </button>
          )}
        </div>

        {/* Letter pane */}
        {draftTab === 'letter' && (
          <>
            <div className={s.formGroup}>
              <label>Select Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                <option value="">Choose a template…</option>
                <optgroup label="Legacy Templates">
                  <option value="PPI_Catalogue_POCS">PPI Catalogue POCS</option>
                  <option value="PPI_Credit_Card_POCS">PPI Credit Card POCS</option>
                  <option value="PPI_Loan_POCS">PPI Loan POCS</option>
                  <option value="PPI_Mortgage_POCS">PPI Mortgage POCS</option>
                  <option value="plev_lba_template">Plevin Letter Before Action</option>
                  <option value="Protective_Claim_Form">Protective Claim Form (PDF)</option>
                  <option value="Substantive_Claim_Form">Substantive Claim Form (PDF)</option>
                  <option value="N215_Cos_form">N215 Certificate of Service</option>
                  <option value="Cover_Letter_to_CNBC_to_serve">
                    Cover Letter to CNBC to Serve
                  </option>
                  <option value="49C_Cover_letter_to_serve">49C Cover Letter to Serve</option>
                  <option value="Protective_Cover_Letter_to_CNBC">
                    Protective Cover Letter to CNBC
                  </option>
                  <option value="Protective_Cover_Letter_to_Lancaster_CC">
                    Protective Cover Letter to Lancaster CC
                  </option>
                </optgroup>
                <optgroup label="── New Templates (Beta) ──">
                  {draftTemplates.map((t) => {
                    const warnIcon =
                      !t.all_mapped && (t as Record<string, unknown>).field_count ? '⚠ ' : '';
                    const recLabel =
                      t.recipient_type === 'client'
                        ? '→ Client'
                        : t.recipient_type === 'defendant'
                          ? '→ Defendant'
                          : '→ Def Sol';
                    return (
                      <option key={t.id} value={`dat_${t.id}`}>
                        {warnIcon}
                        {t.display_name}
                        {t.category ? ` [${t.category}]` : ''} ({recLabel})
                      </option>
                    );
                  })}
                </optgroup>
              </select>
            </div>
            <div className={s.modalActions}>
              <button className={s.btnSecondary} onClick={onClose}>
                Cancel
              </button>
              <button
                className={s.btnPrimary}
                onClick={handleGenerateLetter}
                disabled={!selectedTemplateId || draftGenerating}
              >
                {draftGenerating ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faFileAlt} />
                )}
                Generate
              </button>
            </div>
          </>
        )}

        {/* Email pane */}
        {draftTab === 'email' && canSendEmail && (
          <>
            <div className={s.formGroup}>
              <label>Select Email Template</label>
              <select
                value={selectedEmailTemplateId}
                onChange={(e) => setSelectedEmailTemplateId(e.target.value)}
              >
                <option value="">Choose an email template…</option>
                {emailTemplates.map((t) => {
                  const recLabel =
                    t.recipient_type === 'client'
                      ? '→ Client'
                      : t.recipient_type === 'defendant'
                        ? '→ Defendant'
                        : '→ Def Sol';
                  return (
                    <option key={t.id} value={String(t.id)}>
                      {t.display_name} ({recLabel})
                    </option>
                  );
                })}
              </select>
            </div>
            {selectedEmailTemplateId &&
              (() => {
                const tpl = emailTemplates.find((t) => String(t.id) === selectedEmailTemplateId);
                return tpl ? (
                  <div className={s.emailInfoPanel}>
                    <p>
                      <strong>Recipient:</strong>{' '}
                      {tpl.recipient_type === 'client'
                        ? 'Client'
                        : tpl.recipient_type === 'defendant'
                          ? 'Defendant'
                          : 'Def Solicitor'}
                    </p>
                    <p>
                      <strong>Subject:</strong> {tpl.subject_template || '(no subject)'}
                    </p>
                  </div>
                ) : null;
              })()}
            <div className={s.modalActions}>
              <button className={s.btnSecondary} onClick={onClose}>
                Cancel
              </button>
              <button
                className={s.btnPrimary}
                onClick={handlePreviewEmail}
                disabled={!selectedEmailTemplateId || draftGenerating}
              >
                {draftGenerating ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} />
                )}
                Preview &amp; Send
              </button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}
